import React, { useState, useEffect, useCallback } from "react";
import {
  TextField,
  Button,
  Grid,
  FormHelperText,
  FormControl,
  Typography,
  Paper,
  MenuItem,
  Select,
  InputLabel,
  Box,
  IconButton, Checkbox, FormControlLabel,
} from "@mui/material";
import {
  Person as PersonIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { NotificationManager } from "react-notifications";
import {
  SaveUserRoute,
  UpdateUserRoute,
  GetUserRouteById,
} from "../../../services/UserRoutesService";
import CommonLoader from "../../common/CommonLoader";
import MultiSelectClients from "../../common/MultiSelectClients";
import UserRouteMap from "./UserRouteMap";
import UserSelectorDialog from "../../common/UserSelectorDialog";
import { useNavigate, useParams } from "react-router-dom";

const AddUserRoute = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [selectedUser, setSelectedUser] = useState(null);
  const [userSelectorOpen, setUserSelectorOpen] = useState(false);

  const [userId, setUserId] = useState("");
  const [routeName, setRouteName] = useState("");
  const [routeDate, setRouteDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [routeComments, setRouteComments] = useState("");
  const [routeClients, setRouteClients] = useState("");
  const [isCompleted, setIsCompleted] = useState(false);
  const [routeColor, setRouteColor] = useState("");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [sendRouteEmail, setSendRouteEmail] = useState(true);

  // NEW: child table state
  const [clientStops, setClientStops] = useState([]);

  // Bumped by the Refresh button to force MultiSelectClients to re-pull the client list
  const [clientsRefreshToken, setClientsRefreshToken] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (id) {
      fetchRouteData(parseInt(id, 10));
    }
  }, [id]);

  const fetchRouteData = async (routeId) => {
    setLoading(true);
    try {
      const response = await GetUserRouteById(routeId);
      if (response && response.results) {
        const {
          userId,
          routeName,
          routeDate,
          routeComments,
          routeClients,
          isCompleted,
          routeColor,
          sendRouteEmail,
          userRouteClients, // from child table include
        } = response.results;
        setUserId(userId ? userId.trim() : "");

        if (userId) {
          setSelectedUser({ operator: userId.trim(), aliasName: null });
        }
        setRouteName(routeName || "");
        setRouteDate(
          routeDate
            ? routeDate.split("T")[0]
            : new Date().toISOString().split("T")[0]
        );
        setRouteComments(routeComments || "");
        setRouteClients(routeClients || "");
        setIsCompleted(isCompleted || false);
        setRouteColor(routeColor || "");
        setSendRouteEmail(
          sendRouteEmail === undefined || sendRouteEmail === null
            ? true
            : sendRouteEmail
        );

        // Load existing clientStops from child table if available,
        // otherwise derive from routeClients string with empty notes
        if (userRouteClients && userRouteClients.length > 0) {
          const sorted = [...userRouteClients].sort(
            (a, b) => a.stopNumber - b.stopNumber
          );
          setClientStops(
            sorted.map((s) => ({
              id: s.id, // keep id for update
              clientAccountNumber: s.clientAccountNumber,
              stopNumber: s.stopNumber,
              specialNotes: s.specialNotes || "",
            }))
          );
        } else if (routeClients) {
          // Fallback: build from the string
          const accounts = routeClients.split(",").map((a) => a.trim()).filter(Boolean);
          setClientStops(
            accounts.map((acc, idx) => ({
              clientAccountNumber: acc,
              stopNumber: idx + 1,
              specialNotes: "",
            }))
          );
        }
      } else {
        NotificationManager.error("Failed to fetch route data");
      }
    } catch (error) {
      NotificationManager.error("Error fetching route data");
    } finally {
      setLoading(false);
    }
  };


  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      setClientsRefreshToken((token) => token + 1);
      if (id) {
        await fetchRouteData(parseInt(id, 10));
      }
      NotificationManager.success("Data refreshed");
    } finally {
      setRefreshing(false);
    }
  };

  const handleUserSelect = (user) => {
    setSelectedUser(user);
    setUserId(user.operator.trim());
  };


  const handleRouteClientsChange = useCallback((newAccounts) => {
    setRouteClients(newAccounts);

    const accountList =
      typeof newAccounts === "string"
        ? newAccounts.split(",").map((a) => a.trim()).filter(Boolean)
        : newAccounts || [];

    setClientStops((prev) =>
      accountList.map((acc, idx) => {
        const existing = prev.find((s) => s.clientAccountNumber === acc);
        return {
          ...(existing || {}),
          clientAccountNumber: acc,
          stopNumber: idx + 1,
          specialNotes: existing ? existing.specialNotes || "" : "",
        };
      })
    );
  }, []); // no deps needed — uses only setters which are stable

  // Called by UserRouteMap when drag reorders or a note is saved
  const handleClientStopsChange = (updatedStops) => {
    setClientStops(updatedStops);
    // Keep routeClients string in sync with new order
    const newOrder = updatedStops
      .sort((a, b) => a.stopNumber - b.stopNumber)
      .map((s) => s.clientAccountNumber)
      .join(",");
    setRouteClients(newOrder);
  };

  const validate = () => {
    const newErrors = {};
    if (!userId) newErrors.userId = "User ID is required";
    if (!routeName) newErrors.routeName = "Route Name is required";
    if (!routeDate) newErrors.routeDate = "Route Date is required";
    if (!routeColor) newErrors.routeColor = "Route Color is required";
    if (!routeClients || routeClients.length === 0)
      newErrors.routeClients = "Route Clients are required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    const routeData = {
      id: id ? parseInt(id, 10) : undefined,
      userId,
      routeName,
      routeDate,
      routeComments,
      routeClients,
      isCompleted,
      routeColor,
      sendRouteEmail,
      userRouteClients: clientStops, // send child list to backend
    };

    setLoading(true);
    try {
      if (id) {
        const response = await UpdateUserRoute(routeData);
        if (response.status === 200) {
          NotificationManager.success("Route updated successfully");
        } else {
          throw new Error("Failed to update route");
        }
      } else {
        const response = await SaveUserRoute(routeData);
        if (response.statusCode === 200) {
          NotificationManager.success("Route added successfully");
          navigate("/vlp/user-routes/" + response.results);
        } else {
          throw new Error("Failed to add route");
        }
      }
      window.location.reload();
    } catch (error) {
      NotificationManager.error(error.message || "Error saving route");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (id) {
      if (window.opener) {
        window.close();
      } else {
        navigate("/vlp/user-routes-admin");
      }
    } else {
      navigate("/vlp/user-routes-admin");
    }
  };

  return (
    <>
      <Paper elevation={3} sx={{ padding: 3, margin: "auto" }}>
        <Typography variant="h5" gutterBottom>
          {id ? "Edit Route" : "Add New Route"}
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Route Name"
              value={routeName}
              onChange={(e) => setRouteName(e.target.value)}
              fullWidth
              required
              size="small"
              error={!!errors.routeName}
              helperText={errors.routeName}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              label="Route Date"
              type="date"
              value={routeDate}
              onChange={(e) => setRouteDate(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              required
              size="small"
              error={!!errors.routeDate}
              helperText={errors.routeDate}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <FormControl fullWidth error={!!errors.routeColor}>
              <InputLabel id="route-color-label" style={{ color: routeColor }}>
                Route Color
              </InputLabel>
              <Select
                labelId="route-color-label"
                value={routeColor}
                onChange={(e) => setRouteColor(e.target.value)}
                label="Route Color"
                required
                size="small"
                style={{ color: routeColor }}
              >
                <MenuItem value="">Select a color</MenuItem>
                <MenuItem value="#eb4034" style={{ color: "#eb4034" }}>Red</MenuItem>
                <MenuItem value="#e68f0e" style={{ color: "#e68f0e" }}>Orange</MenuItem>
                <MenuItem value="#050505" style={{ color: "#050505" }}>Black</MenuItem>
                <MenuItem value="#f7e220" style={{ color: "#f7e220" }}>Gold</MenuItem>
                <MenuItem value="#2b66fc" style={{ color: "#2b66fc" }}>Blue</MenuItem>
                <MenuItem value="#7c28eb" style={{ color: "#7c28eb" }}>Purple</MenuItem>
                <MenuItem value="#2cbf1f" style={{ color: "#2cbf1f" }}>Green</MenuItem>
              </Select>
              {errors.routeColor && (
                <FormHelperText error>{errors.routeColor}</FormHelperText>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={4}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {selectedUser ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    p: 1.5,
                    border: 1,
                    borderColor: errors.userId ? "error.main" : "grey.300",
                    borderRadius: 1,
                    backgroundColor: "grey.50",
                    minHeight: "40px",
                  }}
                >
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="body2" fontWeight="medium">
                      {selectedUser.operator}
                      {selectedUser.aliasName &&
                        selectedUser.aliasName !== selectedUser.operator &&
                        ` - ${selectedUser.aliasName}`}
                    </Typography>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() => setUserSelectorOpen(true)}
                    sx={{ color: "primary.main", ml: 1 }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Box>
              ) : (
                <Button
                  variant="outlined"
                  startIcon={<PersonIcon />}
                  onClick={() => setUserSelectorOpen(true)}
                  sx={{
                    justifyContent: "flex-start",
                    textTransform: "none",
                    height: "40px",
                    borderColor: errors.userId ? "error.main" : "grey.300",
                    color: errors.userId ? "error.main" : "inherit",
                  }}
                >
                  Select Driver
                </Button>
              )}
              {errors.userId && (
                <FormHelperText error>{errors.userId}</FormHelperText>
              )}
            </Box>
          </Grid>

          <Grid item xs={12} sm={5}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={sendRouteEmail}
                  onChange={(e) => setSendRouteEmail(e.target.checked)}
                />
              }
              label="Send Route Email"
            />
          </Grid>

          <Grid item xs={12} sm={7}>
            <TextField
              label="Route Comments"
              value={routeComments}
              onChange={(e) => setRouteComments(e.target.value)}
              fullWidth
              multiline
              rows={2}
              size="small"
            />
          </Grid>

          <Grid
            item
            xs={12}
            sm={5}
            sx={{ display: "flex", justifyContent: "flex-end", alignItems: "flex-start" }}
          >
            <Button
              onClick={handleRefresh}
              disabled={refreshing}
              variant="outlined"
              startIcon={<RefreshIcon />}
            >
              {refreshing ? "Refreshing..." : "Refresh"}
            </Button>
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth error={!!errors.routeClients}>
              {/* CHANGED from setRouteClients */}
              <MultiSelectClients
                value={routeClients}
                onChange={handleRouteClientsChange}
                refreshToken={clientsRefreshToken}
              />
              <FormHelperText>{errors.routeClients}</FormHelperText>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <UserRouteMap
              accounts={routeClients}
              onAccountsOrderChange={(newOrder) => {
                setRouteClients(newOrder);
              }}
              clientStops={clientStops}
              onClientStopsChange={handleClientStopsChange}
            />
          </Grid>

          <Grid item xs={12} container justifyContent="flex-end" spacing={2}>
            <Grid item>
              <Button onClick={handleCancel} color="secondary" variant="outlined">
                Cancel
              </Button>
            </Grid>
            <Grid item>
              <Button onClick={handleSave} color="primary" variant="contained">
                {id ? "Update" : "Save"}
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Paper>

      <UserSelectorDialog
        open={userSelectorOpen}
        onClose={() => setUserSelectorOpen(false)}
        onSelect={handleUserSelect}
        multiSelect={false}
        filter="Driver"
      />

      <CommonLoader loading={loading} />
    </>
  );
};

export default AddUserRoute;